import {
  STAGE,
  circleSamples,
  footprintDistance,
  modelFootprint,
  modelStanding,
  type Footprint,
  type ModelFacts,
  type ModelPose,
  type Point,
  type Step,
  type StepModel,
} from '@vrlearning/lesson-schema';

/**
 * Spannend in a headset (ADR-043; spec "Engine behaviour"): the teacher's
 * switch, read only when a step begins; the engine's own warning on the
 * lectern for 3 s before a Spannend part, with "Sla over"; the part; and "Sla
 * over" pressed in the warning or during the part, which puts this pupil's
 * models in the calm end state through a 0.3 s fade -- local only: nothing is
 * sent and nothing is recorded. While a big model is inside the clear space
 * round the stage during the part the lectern fades out (the spike's jaw finding), back when the part
 * ends. Pure, so every state is tested.
 */

export const SPANNEND = {
  /** The warning stands this long before the part begins. */
  warningSeconds: 3,
  /** Out, then the calm end state, then in: each this long. */
  fadeSeconds: 0.3,
  /** The lectern fades out and in over this long. */
  veilSeconds: 0.3,
} as const;

/** The engine's own words (Dutch first, `en` too); never written by the generator. */
export const SPANNEND_TEXT = {
  nl: {
    title: 'Let op: nu wordt het spannend.',
    comes: (labels: readonly string[]): string =>
      labels.length <= 1
        ? `${labels[0] ?? 'Het dier'} komt dichtbij.`
        : `${labels.slice(0, -1).join(', ')} en ${labels.at(-1)!} komen dichtbij.`,
    skip: 'Sla over',
    on: 'Spannend: aan',
    off: 'Spannend: uit',
    ask: 'Spannend',
  },
  en: {
    title: 'Careful: this gets exciting.',
    comes: (labels: readonly string[]): string =>
      labels.length <= 1
        ? `${labels[0] ?? 'The animal'} comes close.`
        : `${labels.slice(0, -1).join(', ')} and ${labels.at(-1)!} come close.`,
    skip: 'Skip',
    on: 'Exciting: on',
    off: 'Exciting: off',
    ask: 'Exciting',
  },
} as const;

/**
 * The teacher's switch as a headset holds it: set whenever a state message
 * says so, read when a step begins. A switch turned on mid-step applies from
 * the next step.
 */
export class SpannendSwitch {
  #on: boolean;
  #entered: boolean;

  constructor(on = false) {
    this.#on = on;
    this.#entered = on;
  }

  /** What the class (or the desktop toggle) says now. */
  set(on: boolean): void {
    this.#on = on;
  }

  get on(): boolean {
    return this.#on;
  }

  /** A step begins: the switch as it is now holds for the whole step. */
  enter(): boolean {
    this.#entered = this.#on;
    return this.#entered;
  }

  /** As read when the step began. */
  get entered(): boolean {
    return this.#entered;
  }
}

/** The step's Spannend part, when the switch was on as it began and the step has one. */
export function spannendPartOf(
  step: Pick<Step, 'spannend'>,
  on: boolean,
): readonly StepModel[] | undefined {
  return on && step.spannend ? step.spannend.models : undefined;
}

/** Whether any step of a lesson has a Spannend part: the start card and the desktop toggle ask then. */
export function hasSpannend(steps: readonly Pick<Step, 'spannend'>[]): boolean {
  return steps.some((step) => step.spannend !== undefined);
}

/** The labels of the objects a part walks to the pupil, for "… komt dichtbij." */
export function comingClose(
  part: readonly StepModel[],
  labelOf: (object: string) => string | undefined,
): string[] {
  return part
    .filter((entry) => entry.walkTo === 'pupil')
    .map((entry) => labelOf(entry.object) ?? entry.object);
}

export type SpannendPhase = 'warning' | 'part' | 'skipped' | 'done';

/**
 * One Spannend part from its warning to its end. `skip` is "Sla over",
 * pressed in the warning or during the part; it does nothing once the part
 * is over (the next step puts every model right).
 */
export class SpannendRun {
  #phase: SpannendPhase = 'warning';
  #t = 0;

  get phase(): SpannendPhase {
    return this.#phase;
  }

  /**
   * "Sla over" is offered: in the warning, during the part, and after it while
   * the step lasts -- a model it brought near is put back by it.
   */
  get skippable(): boolean {
    return this.#phase === 'warning' || this.#phase === 'part' || this.#phase === 'done';
  }

  /** The warning's seconds left; 0 once the part has begun. */
  get warningLeft(): number {
    return this.#phase === 'warning' ? Math.max(0, SPANNEND.warningSeconds - this.#t) : 0;
  }

  /** On by `dt`; true when the part has just begun (its models start now). */
  update(dt: number): boolean {
    if (this.#phase !== 'warning') return false;
    this.#t += dt;
    if (this.#t < SPANNEND.warningSeconds) return false;
    this.#phase = 'part';
    return true;
  }

  /** "Sla over": true when it changed anything (the models fade to the calm end state). */
  skip(): boolean {
    if (!this.skippable) return false;
    this.#phase = 'skipped';
    return true;
  }

  /** Every model of the part has played it. */
  finish(): void {
    if (this.#phase === 'part') this.#phase = 'done';
  }
}

/**
 * Whether the lectern fades out: from a Spannend part's start to the step's
 * end, while a big model's footprint is inside the clear space round the stage
 * (4.5 m from the centre, ADR-061) -- a T. rex standing at the clamp after its bite still has its jaw
 * where the lectern would be. A small model stands on the stage like a shape
 * and never veils it; nor does a static exhibit (`modelStanding`), which
 * stands beside the class and never moves.
 *
 * ADR-084: a calm big model may stand from 3 m, inside that space, all step long. Only a model that takes
 * part in the Spannend part (`inPart`, absent: it does), or one nearer the centre than its calm path brings
 * it (`calmNear`), veils it; one standing calmly at 3.5 m beside another's part does not.
 */
export function lecternVeiled(
  phase: SpannendPhase | undefined,
  models: readonly {
    facts: Pick<ModelFacts, 'box' | 'animations'>;
    scale: number;
    footprint: Footprint;
    inPart?: boolean;
    /** How near the centre its calm path brings it this step, metres (`calmNearest`). */
    calmNear?: number;
  }[],
): boolean {
  if (phase !== 'part' && phase !== 'done') return false;
  return models.some((model) => {
    if (modelStanding(model.facts, model.scale) !== 'big') return false;
    const near = footprintDistance(model.footprint, [0, 0]);
    if (near >= STAGE.clear) return false;
    if (model.inPart !== false) return true;
    // Not in the part: only where it came nearer than its calm path brings it (5 cm, as `moved`).
    return model.calmNear !== undefined && near < model.calmNear - 0.05;
  });
}

/**
 * How near the stage centre a model's calm path brings it in a step (ADR-084), from the calm poses where the
 * step before and this step leave it: a Spannend step performs only its part, so a model the calm step walks
 * away stays at the nearer of the two. On a ring (`circle`), the nearest point of the whole ring, so a model
 * going round 3-4.5 m out does not veil the lectern once a lap.
 */
export function calmNearest(
  facts: ModelFacts,
  scale: number,
  poses: readonly (ModelPose | undefined)[],
): number | undefined {
  let nearest: number | undefined;
  for (const pose of poses) {
    if (pose === undefined) continue;
    const places = pose.circle
      ? circleSamples(pose.circle).map((one) => ({ ...one, clips: [pose.circle!.gait] }))
      : [{ pos: [pose.pos[0], pose.pos[2]] as Point, turn: pose.turn, clips: [pose.animation] }];
    for (const { pos, turn, clips } of places) {
      const near = footprintDistance(modelFootprint(facts, pos, turn, { scale, clips }), [0, 0]);
      nearest = nearest === undefined ? near : Math.min(nearest, near);
    }
  }
  return nearest;
}

/**
 * How far a fade has gone, 0 (as seen) to 1 (gone): out over 0.3 s, the swap
 * at the black, back in over 0.3 s. `swap` is true the frame it is gone.
 */
export class ModelFade {
  #t = 0;
  #swapped = false;

  get done(): boolean {
    return this.#t >= 2 * SPANNEND.fadeSeconds;
  }

  /** Past its middle: what it hides has been put in its end state. */
  get swapped(): boolean {
    return this.#swapped;
  }

  /** On by `dt`: how gone the models are, and whether to put them in their end state now. */
  update(dt: number): { gone: number; swap: boolean } {
    this.#t += dt;
    const f = SPANNEND.fadeSeconds;
    let swap = false;
    if (!this.#swapped && this.#t >= f) {
      this.#swapped = true;
      swap = true;
    }
    const gone = this.#t < f ? this.#t / f : Math.max(0, 1 - (this.#t - f) / f);
    return { gone: Math.min(1, gone), swap };
  }
}
